import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";

import { SIGNED_OUT_EVENT } from "../api/client";
import { getToken, TOKEN_STORAGE_KEY } from "../api/token";
import { useMe, ME_QUERY_KEY } from "../hooks/queries/useMe";
import { useLogout } from "../hooks/queries/useAuthMutations";
import {
  useAthleteCareer,
  useAthletePreferences,
  useLatestAthleteTracking,
} from "../hooks/queries/useAthleteData";
import { useAthletes } from "../hooks/queries/useAthletes";
import { usePrefetchInbox } from "../hooks/queries/useDirectMessages";
import { useLatestScoutTracking } from "../hooks/queries/useScouts";
import { useScoutSearches, useSearchMatches } from "../hooks/queries/useScoutSearches";
import { isFootballAthlete } from "../constants/footballTaxonomy";

const AuthContext = createContext(null);

// Provider over the self-hosted backend. Public surface is unchanged from the
// supabase era so guards and screens keep working; data now flows through
// react-query (session bootstrap via GET /auth/me).
export const AuthProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const [hasToken, setHasToken] = useState(() => Boolean(getToken()));

  const me = useMe({ enabled: hasToken });
  usePrefetchInbox(hasToken);
  const role = me.data?.role ?? null;
  const profile = me.data?.profile ?? null;
  const isAuthenticated = Boolean(me.data);
  const authChecked = !hasToken || me.isFetched || me.isError;

  // ---- local gate state --------------------------------------------------------
  // Only flags that steer the signup/onboarding gates live here. Profile and
  // tracking data always come from the server: the context used to keep saved
  // copies that took priority over refetched data until sign-out, which hid
  // edits made on another device and let the next save overwrite them.
  // undefined = derive from server; value = local override (flips the gate
  // without waiting for a refetch).
  const [pendingEvalLocal, setPendingEvalLocal] = useState(undefined);
  const [pendingConsentData, setPendingConsentData] = useState(null);
  const [activeSearchId, setActiveSearchId] = useState(null);

  const resetLocalState = useCallback(() => {
    setPendingEvalLocal(undefined);
    setPendingConsentData(null);
    setActiveSearchId(null);
  }, []);

  // ---- athlete data -----------------------------------------------------------
  const isAthlete = role === "athlete";
  const athleteId = isAthlete ? profile?.id : null;
  const careerQuery = useAthleteCareer(athleteId);
  const preferencesQuery = useAthletePreferences(athleteId);
  const trackingQuery = useLatestAthleteTracking(athleteId);

  // athlete_tracking[0] holds the newest value of each metrics section (see
  // useLatestAthleteTracking), not one day's row. Day-scoped data — today's
  // meals and check-in — is read per date with useAthleteTrackingDay.
  const athleteData = useMemo(() => {
    if (!isAthlete || !profile) return null;
    return {
      ...profile,
      athlete_career: careerQuery.data ? [careerQuery.data] : [],
      athlete_preferences: preferencesQuery.data ? [preferencesQuery.data] : [],
      athlete_tracking: trackingQuery.data ? [trackingQuery.data] : [],
    };
  }, [isAthlete, profile, careerQuery.data, preferencesQuery.data, trackingQuery.data]);

  // ---- scout data ---------------------------------------------------------------
  const isScout = role === "scout";
  const scoutId = isScout ? profile?.id : null;
  const scoutTrackingQuery = useLatestScoutTracking(scoutId);
  const athletesQuery = useAthletes({}, { enabled: isScout, staleTime: 5 * 60_000, refetchOnWindowFocus: false });
  const searchesQuery = useScoutSearches({ enabled: isScout });
  const scoutSearches = useMemo(
    () => (isScout ? searchesQuery.data || [] : []),
    [isScout, searchesQuery.data],
  );

  // Default the active search to the most recent one.
  useEffect(() => {
    if (!isScout) return;
    if (activeSearchId && scoutSearches.some((s) => s.id === activeSearchId)) return;
    setActiveSearchId(scoutSearches[0]?.id ?? null);
  }, [isScout, scoutSearches, activeSearchId]);

  const matchesQuery = useSearchMatches(activeSearchId, { enabled: isScout });
  // Solo fútbol: los perfiles legados de otros deportes no llegan a los scouts.
  const availableAthletes = useMemo(
    () => (isScout ? (matchesQuery.data || []).filter(isFootballAthlete) : []),
    [isScout, matchesQuery.data],
  );

  // Scout list decorated with the active search's score/reasons.
  const linkedAthletes = useMemo(() => {
    if (!isScout) return [];
    const rows = (athletesQuery.data?.items || []).filter(isFootballAthlete);
    if (!availableAthletes.length) return rows;
    const matchById = new Map(
      availableAthletes.map((r) => [String(r.id), { score: r.score, reasons: r.reasons }]),
    );
    return rows.map((row) => {
      const m = matchById.get(String(row.id || row.athlete_id));
      return m
        ? { ...row, score: m.score, reasons: m.reasons }
        : { ...row, score: undefined, reasons: undefined };
    });
  }, [isScout, athletesQuery.data, availableAthletes]);

  const scoutData = isScout ? profile : null;

  const scoutTrackingData = useMemo(() => {
    if (!isScout) return null;
    return scoutTrackingQuery.data ?? { metrics: {} };
  }, [isScout, scoutTrackingQuery.data]);

  // ---- derived flags -------------------------------------------------------------
  const pendingEvaluationAthleteId =
    pendingEvalLocal !== undefined
      ? pendingEvalLocal
      : me.data?.needsEvaluation && isAthlete
        ? (profile?.id ?? null)
        : null;

  const isLoading =
    (hasToken && !me.isFetched) ||
    (isAthlete && trackingQuery.isLoading) ||
    (isScout && (scoutTrackingQuery.isLoading || athletesQuery.isLoading || searchesQuery.isLoading));

  // ---- session lifecycle -----------------------------------------------------------
  const logoutMutation = useLogout();

  const signOut = useCallback(async () => {
    await logoutMutation.mutateAsync().catch(() => {});
    resetLocalState();
    setHasToken(false);
  }, [logoutMutation, resetLocalState]);

  useEffect(() => {
    // 401 from any request, or the token cleared in another tab -> local sign-out.
    const onSignedOut = () => {
      resetLocalState();
      setHasToken(false);
      queryClient.clear();
    };
    const onStorage = (e) => {
      if (e.key !== TOKEN_STORAGE_KEY) return;
      if (!getToken()) onSignedOut();
      else setHasToken(true);
    };
    window.addEventListener(SIGNED_OUT_EVENT, onSignedOut);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(SIGNED_OUT_EVENT, onSignedOut);
      window.removeEventListener("storage", onStorage);
    };
  }, [queryClient, resetLocalState]);

  // ---- compatibility API (same names the screens already call) ----------------------
  // Login/signup mutations store the token and seed the me-query themselves;
  // these callbacks flip local gate state exactly like the supabase-era context.
  const signIn = useCallback(() => {
    setHasToken(true);
  }, []);

  const completeAthleteSignup = useCallback((athleteRowId, consentData = null) => {
    setPendingEvalLocal(athleteRowId);
    setPendingConsentData(consentData);
    setHasToken(true);
  }, []);

  const completeScoutSignup = useCallback(() => {
    setHasToken(true);
  }, []);

  const completeConsent = useCallback(() => {
    setPendingConsentData(null);
  }, []);

  // Resolves once the profile the evaluation just saved has been refetched, so
  // the dashboard never renders the pre-onboarding profile.
  const completeOnboarding = useCallback(async () => {
    setPendingEvalLocal(null);
    await Promise.all([
      queryClient.refetchQueries({ queryKey: ME_QUERY_KEY }),
      queryClient.invalidateQueries({ queryKey: ["athlete-career"] }),
      queryClient.invalidateQueries({ queryKey: ["athlete-preferences"] }),
    ]);
  }, [queryClient]);

  const refreshScoutSearches = useCallback(
    async (makeActiveId) => {
      await queryClient.invalidateQueries({ queryKey: ["scout-searches"] });
      if (makeActiveId) setActiveSearchId(makeActiveId);
    },
    [queryClient],
  );

  const value = useMemo(
    () => ({
      authChecked,
      isAuthenticated,
      authUserId: me.data?.user?.id ?? null,
      userRole: role,
      pendingEvaluationAthleteId,
      pendingConsentData,
      athleteData,
      scoutData,
      scoutTrackingData,
      linkedAthletes,
      scoutSearches,
      activeSearchId,
      availableAthletes,
      isLoading,
      setActiveSearchId,
      refreshScoutSearches,
      completeOnboarding,
      completeConsent,
      signIn,
      completeAthleteSignup,
      completeScoutSignup,
      signOut,
    }),
    [
      authChecked,
      isAuthenticated,
      me.data,
      role,
      pendingEvaluationAthleteId,
      pendingConsentData,
      athleteData,
      scoutData,
      scoutTrackingData,
      linkedAthletes,
      scoutSearches,
      activeSearchId,
      availableAthletes,
      isLoading,
      refreshScoutSearches,
      completeOnboarding,
      completeConsent,
      signIn,
      completeAthleteSignup,
      completeScoutSignup,
      signOut,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return context;
};
